import { Buffer } from 'buffer';
import split from 'lodash/split';

import { AnalyzerManager, isPrintingWithLineFeedCommand, possiblyDividerText } from '../analyzer';
import { PrinterContext } from '../renderer/printer';
import { walkAST } from '../renderer/util';
import { AnalyzerGuide, AstNode } from '../types';

const unescapeSpecialChars = (text: string) => {
  return text.replace(/\\n/g, '\n').replace(/\\r/g, '\r').replace(/\\t/g, '\t');
};

export const convertTextToBinaryFormat = (textFormatData: string) => {
  textFormatData = unescapeSpecialChars(textFormatData);

  let binaryData = '';
  for (const char of split(textFormatData, '')) {
    const encodedChar = encodeURIComponent(char);
    const lengthOfURIEncodedChar = encodedChar.match(/%.{2}/g)?.length;

    if (!lengthOfURIEncodedChar) binaryData += encodedChar;
    else if (lengthOfURIEncodedChar !== char.length) {
      // Handle unicode character

      // Inject ESC U N I C O D E command
      [27, 85, 78, 73, 67, 79, 68, 69].forEach((code) => {
        binaryData += String.fromCharCode(code);
      });

      binaryData += char + '\x00';
    } else binaryData += String.fromCharCode(parseInt(encodedChar.replace('%', ''), 16));
  }

  return Buffer.from(binaryData);
};

export const convertBufferToStringBytes = (bufferData: Buffer) => {
  const bytes: string[] = [];
  bufferData.forEach((element) => {
    bytes.push(String.fromCharCode(element));
  });

  return bytes;
};

export const convertEscPosToJsonObject = ({
  ast,
  possibleMaxWidth,
  analyzerGuide = undefined,
  isDynamicData = false,
}: {
  ast: AstNode[];
  possibleMaxWidth: number;
  analyzerGuide?: AnalyzerGuide;
  isDynamicData?: boolean;
}) => {
  let latestASTIndex = -1;
  const analyzerManager = new AnalyzerManager(analyzerGuide, ast, possibleMaxWidth);

  for (const [astIndex, node, textData] of walkAST(ast, new PrinterContext())) {
    if (isPrintingWithLineFeedCommand(node.command)) analyzerManager.incrementLineNumber();

    // Skip parsing ast node until the latest parsed index
    if (latestASTIndex > astIndex) continue;

    // Skip parsing divider text automatically so no need to put this into analyzerGuide
    if (textData.length === 0 || possiblyDividerText(textData, possibleMaxWidth)) continue;

    if (isDynamicData) {
      latestASTIndex = analyzerManager.extractDataFromTextDynamically(textData, astIndex);
    } else {
      latestASTIndex = analyzerManager.extractDataFromText(textData, astIndex).latestASTIndex;
    }

    if (latestASTIndex === -1) break;
  }

  return {
    parsedData: {
      ...analyzerManager.parsedObjects,
    },
    unparsedData: analyzerManager.unparsedTexts,
  };
};
